/**
 * 全局比赛详情仓库
 * 用于批量获取和缓存比赛详情，避免重复请求
 * 集成 WebSocket 实时更新比赛数据
 */
import { createSharedRoot } from '@bz/request';
import { createSignal, createEffect, createMemo, onCleanup, Accessor } from 'solid-js';
import { createAsync } from '@solidjs/router';
import { useSocketBusiness } from '@bz/socket';
import type { ApiResponse } from '@bz/request/src/apis';

type MatchDetail = ApiResponse<'matchDetail'>;

interface MatchDetailsStore {
  /** 比赛详情缓存 Map */
  detailsMap: Map<string, MatchDetail>;
  /** 待加载的比赛 ID 集合 */
  pendingIds: Set<string>;
  /** 是否正在加载 */
  isLoading: boolean;
  /** 已订阅 WebSocket 的比赛 ID 集合 */
  subscribedIds: Set<string>;
}

/**
 * 创建全局比赛详情仓库
 */
const useMatchDetailsStore = createSharedRoot(() => {
  const getMatchDetails = useAction(post.matchDetails);
  const { useMatchPush } = useSocketBusiness();

  const [store, setStore] = createSignal<MatchDetailsStore>({
    detailsMap: new Map(),
    pendingIds: new Set(),
    isLoading: false,
    subscribedIds: new Set(),
  });

  // WebSocket 订阅管理 Map
  const subscriptions = new Map<string, ReturnType<typeof useMatchPush>>();

  /**
   * 订阅单个比赛的 WebSocket 推送
   * @param matchId 比赛 ID
   */
  const subscribeMatch = (matchId: string) => {
    // 避免重复订阅
    if (subscriptions.has(matchId)) {
      return;
    }

    console.log(`[MatchStore] 订阅比赛 WebSocket: ${matchId}`);

    // 创建 WebSocket 订阅
    const matchData = useMatchPush(() => matchId);
    subscriptions.set(matchId, matchData);

    // 监听 WebSocket 数据变化，自动更新 Store
    createEffect(() => {
      const data = matchData();
      if (data) {
        console.log(`[MatchStore] WebSocket 更新比赛 ${matchId}:`, data);

        // 更新 Store 中的比赛数据
        setStore((prev) => {
          const newDetailsMap = new Map(prev.detailsMap);
          const existingDetail = newDetailsMap.get(matchId);

          // 合并 WebSocket 数据和现有详情
          // WebSocket 数据优先级更高（实时数据）
          const updatedDetail = {
            ...existingDetail,
            ...data,
            oldStatus: existingDetail?.status,
            status: data.sportEventStatus?.status || existingDetail?.status,
          } as MatchDetail;

          newDetailsMap.set(matchId, updatedDetail);

          return {
            ...prev,
            detailsMap: newDetailsMap,
            subscribedIds: new Set([...prev.subscribedIds, matchId]),
          };
        });
      }
    });
  };

  /**
   * 批量订阅比赛 WebSocket
   * @param matchIds 比赛 ID 数组
   */
  const subscribeMatches = (matchIds: string[]) => {
    if (!matchIds || matchIds.length === 0) return;

    console.log(`[MatchStore] 批量订阅 ${matchIds.length} 场比赛`);
    matchIds.forEach((id) => subscribeMatch(id));
  };

  /**
   * 取消订阅比赛
   * @param matchId 比赛 ID
   */
  const unsubscribeMatch = (matchId: string) => {
    subscriptions.delete(matchId);

    setStore((prev) => {
      const newSubscribedIds = new Set(prev.subscribedIds);
      newSubscribedIds.delete(matchId);
      return {
        ...prev,
        subscribedIds: newSubscribedIds,
      };
    });

    console.log(`[MatchStore] 取消订阅比赛: ${matchId}`);
  };

  /**
   * 批量预加载比赛详情 + 自动订阅 WebSocket
   * @param matchIds 比赛 ID 数组
   */
  const prefetchMatchDetails = async (matchIds: string[]) => {
    if (!matchIds || matchIds.length === 0) return;

    // 标记为加载中
    setStore((prev) => ({
      ...prev,
      isLoading: true,
      pendingIds: new Set([...prev.pendingIds, ...matchIds]),
    }));

    try {
      // 调用批量接口，获取所有传入的 matchIds 的详情
      const response = await getMatchDetails({ matchIds });

      // 响应格式: MatchDetail[]
      if (response && Array.isArray(response)) {
        // 更新缓存 - 用新获取的数据整体更新
        setStore((prev) => {
          const newDetailsMap = new Map(prev.detailsMap);
          response.forEach((detail: any) => {
            if (detail && detail.id) {
              newDetailsMap.set(String(detail.id), detail);
            }
          });

          // 从待加载集合中移除已加载的 ID
          const newPendingIds = new Set(prev.pendingIds);
          matchIds.forEach((id) => newPendingIds.delete(id));

          return {
            ...prev,
            detailsMap: newDetailsMap,
            pendingIds: newPendingIds,
            isLoading: newPendingIds.size > 0,
          };
        });

        // 自动订阅这些比赛的 WebSocket 推送
        subscribeMatches(matchIds);
      }
    } catch (error) {
      console.error('批量获取比赛详情失败:', error);
      // 失败时也要清除待加载标记
      setStore((prev) => {
        const newPendingIds = new Set(prev.pendingIds);
        matchIds.forEach((id) => newPendingIds.delete(id));
        return {
          ...prev,
          pendingIds: newPendingIds,
          isLoading: newPendingIds.size > 0,
        };
      });
    }
  };

  /**
   * 从仓库获取比赛详情（响应式）
   * @param matchId 比赛 ID
   * @returns 比赛详情或 undefined（会自动更新）
   */
  const getMatchDetail = (matchId: string): MatchDetail | undefined => {
    return store().detailsMap.get(matchId);
  };

  //  用于批量获取比赛详情（非响应式）
  const getMatchDetailsArray = (matchIds: string[]): (MatchDetail | undefined)[] => {
    return matchIds.map((id) => store().detailsMap.get(id));
  };

  /**
   * 创建响应式的比赛详情访问器
   * @param matchId 比赛 ID 访问器
   * @returns 响应式的比赛详情
   *
   * @example
   * const matchDetail = createMatchDetailMemo(() => selection.matchId);
   * // 使用: matchDetail()?.homeScore  // 自动更新
   */
  const createMatchDetailMemo = (matchId: Accessor<string>) => {
    return createMemo(() => {
      const id = matchId();
      return store().detailsMap.get(id);
    });
  };

  /**
   * 检查比赛详情是否存在
   * @param matchId 比赛 ID
   * @returns 是否存在
   */
  const hasMatchDetail = (matchId: string): boolean => {
    return store().detailsMap.has(matchId);
  };

  /**
   * 检查是否已订阅 WebSocket
   * @param matchId 比赛 ID
   * @returns 是否已订阅
   */
  const isSubscribed = (matchId: string): boolean => {
    return store().subscribedIds.has(matchId);
  };

  /**
   * 清空仓库并取消所有订阅
   */
  const clear = () => {
    // 清除所有 WebSocket 订阅
    subscriptions.clear();

    setStore({
      detailsMap: new Map(),
      pendingIds: new Set(),
      isLoading: false,
      subscribedIds: new Set(),
    });

    console.log('[MatchStore] 清空仓库并取消所有订阅');
  };

  // 组件卸载时清理所有订阅
  onCleanup(() => {
    console.log('[MatchStore] 清理所有 WebSocket 订阅');
    subscriptions.clear();
  });

  return {
    store,
    prefetchMatchDetails,
    getMatchDetail,
    getMatchDetailsArray,
    createMatchDetailMemo,
    hasMatchDetail,
    subscribeMatch,
    subscribeMatches,
    unsubscribeMatch,
    isSubscribed,
    clear,
  };
});

/**
 * 优化版的 useMatchDetail Hook
 * 优先从全局仓库获取，如果不存在则使用原始方法
 * 自动获取实时更新的比赛数据
 */
export const useOptimizedMatchDetail = (matchId: Accessor<string | number>): Accessor<MatchDetail | undefined> => {
  const { hasMatchDetail, createMatchDetailMemo } = useMatchDetailsStore();

  // 使用 createMatchDetailMemo 创建响应式访问器
  const matchDetail = createMatchDetailMemo(() => String(matchId()));

  // 从原始方法获取的结果（作为兜底）
  const fallbackDetail = createAsync(async () => {
    const id = String(matchId());
    // 只有当仓库中不存在时才请求
    if (!hasMatchDetail(id)) {
      return await get.matchDetail({ matchId: id });
    }
    return undefined;
  });

  // 合并两个数据源
  return createMemo(() => {
    const cached = matchDetail();
    if (cached) {
      return cached;
    }
    return fallbackDetail();
  });
};

export { useMatchDetailsStore };
